import React, { Suspense, useEffect, useRef, useState, useContext } from "react";
import { matchPath, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { MuiBox, MuiTypography } from "components/atoms";
import { DriveHeader, DriveSidebar } from "components/organisms";
import ContentLoading from "components/fallbackUI/ContentLoading";
import { useMediaQuery } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { resetFilterValues } from "store/slices/general";
import {
  GetDrivePrefrenceAction,
  GetProfileAction,
  ManageDrivePrefrenceAction,
} from "store/middlewares/profile";
import { UploadFileLoader, UploadFolderLoader } from "components/molecules";
import {
  GetSettingAction,
} from "store/middlewares/general";
import { useTranslation } from "react-i18next";
import LocalstorageService from "utils/localstorage-services";
import { getDeviceId } from "utils/MultiLoginFunctions";
import { openToast } from "store/slices/toast.slice";
import { CheckFileExistAction, UploadFilesAction } from "store/middlewares/files";
import {
  uploadFileHandle,
} from "store/slices/general";
import { getFilesFromDataTransfer } from "utils/fileHelpers";
import { FirebaseContext } from "context/FirebaseContext";
import { FilterContext } from "context/FilterContext";
import { getFileExtension } from "utils/fileExtension";
import { getCookie, setCookie } from "utils/cookie";
import useFCMToken from "hooks/useFCMToken";
import { openCenterModal } from "store/slices/CenterModal.slice";
import UploadOptionsModal from "components/molecules/UploadOptionsModal";
import { checkAndUpdateTimestamps } from "utils/checkAndUpdateTimestamps";

// Drop zone component – wraps the main content area
const DropZone = ({ children, folderId, onUploadSuccess, disabled = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isDragOver, setIsDragOver] = useState(false);

  const dragCounter = useRef(0);

  // Prevent duplicate upload error toasts for the same
  // drag-and-drop upload batch.
  const uploadErrorRef = useRef(new Set());

  const { settings } = useContext(FirebaseContext);
  const { setting } = useSelector((state) => state.general);
  const { filterData } = useContext(FilterContext);
  const { drivePrefrences } = useSelector((state) => state.profile);
  const { requestToken } = useFCMToken({ autoRequest: false });
  const { homeFilesFolder } = useSelector((state) => state.general);



  useEffect(() => {
    if (drivePrefrences?.drive_notification === "both" || drivePrefrences?.drive_notification === "push") {
      if (
        !drivePrefrences?.fcm_token ||
        drivePrefrences?.fcm_token === "fcm_token"
      ) {
        requestToken().then(({ token, reason }) => {
          if (reason !== "granted" || !token) return;
          dispatch(ManageDrivePrefrenceAction({ fcmToken: token }));
        });
      }
    }
  }, [drivePrefrences, dispatch, requestToken]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (disabled) return;
    dragCounter.current += 1;
    setIsDragOver(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (disabled) return;
    // Ensure we keep the highlight while dragging
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (disabled) return;
    dragCounter.current -= 1;

    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  };
  // ----- Upload helpers (check exists -> modal -> upload) -----
  const buildName = (name, i) => {
    const dot = name.lastIndexOf(".");
    const base = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : "";
    return `${base} (${i})${ext}`;
  };

  const fileExists = (fileName) =>
    dispatch(
      CheckFileExistAction({
        fileName,
        ...(folderId && { folderId }),
      }),
    ).then((result) => {
      const fileId = result?.payload?.response?.data?.id || result?.payload?.response?.file?.id || result?.payload?.response?.id;
      return {
        exists: Boolean(result?.payload?.response?.exists),
        fileId: fileId || null,
      };
    });

  const findFileIdByName = (fileName) => {
    const allFiles = [
      ...(homeFilesFolder?.my_files?.data || []),
      ...(homeFilesFolder?.shared_with_me?.data?.map(item => item?.file).filter(Boolean) || []),
    ];
    const file = allFiles.find(f => f.file_name === fileName || f.name === fileName);
    return file?.id || null;
  };


  const handleUploadError = (res) => {
    const errorKey = String(res);
    if (uploadErrorRef.current.has(errorKey)) return;
    uploadErrorRef.current.add(errorKey);

    if (res === 2005) {
      dispatch(
        openToast({
          type: "error",
          message: t("errorCode.2005", { value: settings?.maxFileSize }),
        }),
      );
    } else if (res === 2001) {
      dispatch(openToast({ type: "error", message: t("errorCode.2001") }));
    } else {
      dispatch(openToast({ type: "error", message: t("validation.uploadFailed") }));
    }
  };

  // replace = true -> backend deletes the old file and uploads the new one
  const sendFiles = (files, replace = false, oldFileId = null) => {
    if (!files.length) return;

    const filesWithTempIds = files.map((file) => ({
      tempId: crypto.randomUUID(),
      name: file.name,
      fileRef: file,
    }));

    dispatch(
      uploadFileHandle({
        files: filesWithTempIds.map(({ tempId, name, fileRef }) => ({
          tempId,
          name,
          fileRef,
          folderId: folderId || null,
          isReplaced: replace,
          oldFileId: replace ? oldFileId : null,
        })),
        display: true,
      }),
    );

    filesWithTempIds.forEach(({ tempId, fileRef }) => {
      const formData = new FormData();
      formData.append("files", fileRef);
      formData.append("type", getFileExtension(fileRef));
      if (folderId) formData.append("folderId", folderId);
      if (replace) formData.append("isReplaced", "true");

      dispatch(
        UploadFilesAction({
          formData,
          tempId,
          oldFileId: replace ? oldFileId : null,
          sortBy: filterData?.sortBy,
          sortOrder: filterData?.sortOrder,
          onSuccess: () => onUploadSuccess?.(),
          onError: handleUploadError,
        }),
      );
    });
  };

  const getRenamedFile = async (file) => {
    // First check locally in Redux state for performance
    const existingNames = new Set([
      ...(homeFilesFolder?.my_files?.data?.map(f => f.file_name || f.name) || []),
      ...(homeFilesFolder?.shared_with_me?.data?.map(item => item?.file?.file_name || item?.file?.name).filter(Boolean) || []),
    ]);

    for (let i = 1; i <= 50; i++) {
      const newName = buildName(file.name, i);
      if (!existingNames.has(newName)) {
        // Double-check with backend to be safe
        const exists = await fileExists(newName);
        if (!exists.exists) {
          return new File([file], newName, { type: file.type });
        }
      }
    }
    return file;
  };

  const uploadWithCheck = async (files) => {
    const results = await Promise.all(files.map((f) => fileExists(f.name)));
    const newFiles = files.filter((_, i) => !results[i].exists);
    const duplicates = files.filter((_, i) => results[i].exists);

    sendFiles(newFiles);

    if (duplicates.length > 0) {
      dispatch(
        openCenterModal({
          content: (
            <UploadOptionsModal
              fileName={
                duplicates.length === 1
                  ? duplicates[0].name
                  : `${duplicates.length} files`
              }
              onConfirm={async (option) => {
                uploadErrorRef.current.clear();
                if (option === "replace") {
                  duplicates.forEach((file) => {
                    const originalIndex = files.indexOf(file);
                    let fileId = results[originalIndex]?.fileId;
                    // Fallback: search by name if backend doesn't return ID
                    if (!fileId) {
                      fileId = findFileIdByName(file.name);
                    }
                    sendFiles([file], true, fileId);
                  });
                } else {
                  // Rename files in parallel for better performance
                  const renamed = await Promise.all(duplicates.map(f => getRenamedFile(f)));
                  sendFiles(renamed);
                }
              }}
            />
          ),
        }),
      );
    }
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    if (disabled) return;

    dragCounter.current = 0;
    setIsDragOver(false);

    // Start a new error-deduplication batch for this drop.
    uploadErrorRef.current.clear();

    const maxDriveStorage = Number(setting?.max_drive_storage || 0);
    const usedDriveStorage = Number(setting?.used_drive_storage || 0);
    const remainingSpace = maxDriveStorage - usedDriveStorage;

    const dataTransfer = e.dataTransfer;
    if (!dataTransfer) return;

    try {
      const files = await getFilesFromDataTransfer(dataTransfer);
      if (files.length === 0) return;

      const fileSizeLimit = settings?.maxFileSize;
      const fileUploadLimit = settings?.maxFileCount;

      if (files.length > fileUploadLimit) {
        dispatch(
          openToast({
            type: "error",
            message: t("validation.fileUploadLimitMsg", { value: fileUploadLimit }),
          }),
        );
        return;
      }

      const totalUploadSize = files.reduce((total, file) => total + file.size, 0);

      if (totalUploadSize > remainingSpace * 1024 * 1024) {
        dispatch(
          openToast({
            type: "error",
            message: t("validation.driveNotEnoughStorage", {
              required: (totalUploadSize / (1024 * 1024)).toFixed(2),
              available: remainingSpace.toFixed(2),
            }),
          }),
        );
        return;
      }

      const hasOversizedFile = files.some(
        (file) => file.size > fileSizeLimit * 1024 * 1024,
      );

      if (hasOversizedFile) {
        const errorKey = "file-size-limit";
        if (!uploadErrorRef.current.has(errorKey)) {
          uploadErrorRef.current.add(errorKey);
          dispatch(
            openToast({
              type: "error",
              message: t("errorCode.2005", { value: fileSizeLimit }),
            }),
          );
        }
      }

      const validFiles = files.filter(
        (file) => file.size <= fileSizeLimit * 1024 * 1024,
      );

      if (validFiles.length === 0) return;

      await uploadWithCheck(validFiles);
    } catch (error) {
      const errorKey = "upload-failed";
      if (uploadErrorRef.current.has(errorKey)) return;
      uploadErrorRef.current.add(errorKey);

      dispatch(openToast({ type: "error", message: t("validation.uploadFailed") }));
    }
  };

  return (
    <MuiBox
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        position: "relative",
        height: "100%",
        width: "100%",
        // Highlight when dragging over
        border: isDragOver && !disabled
          ? "2px dashed #1976d2"
          : "2px dashed transparent",
        borderRadius: "8px",
        transition: "border 0.2s ease",
        backgroundColor: isDragOver && !disabled
          ? "rgba(25, 118, 210, 0.04)"
          : "transparent",
      }}
    >
      {children}

      {/* Optional overlay for extra feedback */}
      {isDragOver && !disabled && (
        <MuiBox
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            borderRadius: "8px",
          }}
        >
          <MuiBox
            sx={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "32px",
              textAlign: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              width: "fit-content",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <MuiTypography fontWeight={700}>{t("common.dropFilesHere")}</MuiTypography>
            <MuiTypography>{t("common.dropFilesOrFolders")}</MuiTypography>
          </MuiBox>
        </MuiBox>
      )}
    </MuiBox>
  );
};

const DriveLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMdDown = useMediaQuery("(max-width:1200px)");
  const { uploadNewFile, uploadFolderState } = useSelector((state) => state.general);
  const { i18n } = useTranslation();
  const location = useLocation();

  const existingInfo =
    JSON.parse(localStorage.getItem("bigfoot-otherInfo")) || {};

  const [deviceId] = useState(getDeviceId());

  const { settings, configData, apiTimestamps, setApiTimestamps } = useContext(FirebaseContext);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { folderId } = useParams();

  const { driveData } = useSelector((state) => state.folders);

  // Extract the folder ID from the current route (e.g. /drive/folder/:id)
  const folderRouteMatch = matchPath("/drive/folder/:id", location.pathname);
  const routeFolderId = folderRouteMatch?.params?.id;

  const isViewOnly =
    driveData?.folder?.permission === "view" &&
    driveData?.folder?.id === routeFolderId;

  useEffect(() => {
    const preventContextMenu = (e) => {
      e.preventDefault();
    };

    window.addEventListener("contextmenu", preventContextMenu);

    const supportedLanguages = ["en", "gu", "hi", "fr"];

    const browserLang = (
      navigator.language ||
      navigator.userLanguage ||
      "en"
    ).split("-")[0];

    const defaultLanguage = supportedLanguages.includes(browserLang)
      ? browserLang
      : settings?.default_language;

    const language = getCookie("webLanguage") || defaultLanguage;

    setCookie("webLanguage", language);
    i18n.changeLanguage(language);

    return () => {
      window.removeEventListener("contextmenu", preventContextMenu);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    dispatch(GetProfileAction());
    dispatch(GetSettingAction());
    dispatch(GetDrivePrefrenceAction());
  }, [dispatch]);

  useEffect(() => {
    if (!configData) return;

    checkAndUpdateTimestamps(
      configData,
      apiTimestamps,
      setApiTimestamps,
      dispatch,
    );
  }, [configData, apiTimestamps, setApiTimestamps, dispatch]);

  useEffect(() => {
    if (!existingInfo.deviceId) {
      LocalstorageService.defaultOtherUserInfo({
        ...existingInfo,
        deviceId,
      });
    }
  }, [deviceId]);

  // Reset filter values when navigating back (popstate)
  useEffect(() => {
    const handlePopState = () => {
      dispatch(resetFilterValues());
    };

    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, [dispatch]);

  const handleUploadSuccess = () => {
    if (folderId == null) {
      navigate("/drive/mydrive");
    }
  };

  return (
    <MuiBox
      className="no-scrollbar"
      sx={{
        height: "100vh",
        display: "flex",
        overflow: "hidden",
        backgroundColor: "background.gray200",
      }}
    >
      {!isMdDown && <DriveSidebar />}

      <MuiBox
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <DriveHeader />

        <MuiBox
          className="no-scrollbar"
          sx={{
            flex: 1,
            overflowY: "auto",
            height: "100%",
            p: "16px",
          }}
        >
          <MuiBox
            sx={{
              backgroundColor: "background.white",
              borderRadius: "8px",
              boxShadow: "0px 0px 0px 1px #00000014",
              p: "24px",
              height: "100%",
              overflow: "hidden",
            }}
          >
            {/* Wrap the Outlet with the drop zone */}
            <DropZone
              folderId={folderId}
              onUploadSuccess={handleUploadSuccess}
              disabled={isViewOnly}
            >
              <MuiBox
                sx={{
                  height: "100%",
                  overflow: "auto",
                  flex: 1,
                }}
                className="no-scrollbar no-text-select"
              >
                {/* Boundary sits below the header/sidebar so a lazily loaded
                    page only swaps the content area, never the whole shell. */}
                <Suspense fallback={<ContentLoading />}>
                  <Outlet />
                </Suspense>
              </MuiBox>
            </DropZone>
          </MuiBox>
        </MuiBox>
      </MuiBox>

      {/* Upload loaders – stack at bottom-right corner */}
      {(uploadFolderState?.display || uploadNewFile?.display) && (
        <MuiBox
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            width: "100%",
            maxWidth: 400,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {uploadFolderState?.display && <UploadFolderLoader />}
          {uploadNewFile?.display && <UploadFileLoader />}
        </MuiBox>
      )}
    </MuiBox>
  );
};

export default DriveLayout;